import React, { forwardRef } from 'react';
import { cn } from '../../../utils/cn';

/**
 * SectionReveal - Animation wrapper for smooth content reveals
 *
 * A layout component that wraps content with a fade-in-up animation effect.
 * Useful for creating staggered reveal animations across page sections.
 *
 * @example
 * ```tsx
 * <SectionReveal delay={0}>
 *   <Header />
 * </SectionReveal>
 *
 * <SectionReveal delay={0.1}>
 *   <ContentSection />
 * </SectionReveal>
 *
 * <SectionReveal delay={0.2}>
 *   <Footer />
 * </SectionReveal>
 * ```
 */

export interface SectionRevealProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Animation delay in seconds
   * Common values: 0, 0.1, 0.15, 0.2, 0.3
   */
  delay?: number;
  /** Additional CSS classes */
  className?: string;
  /** Content to animate */
  children: React.ReactNode;
}

/**
 * SectionReveal Component
 *
 * Features:
 * - Smooth fade-in-up animation using CSS keyframes
 * - Configurable animation delay for staggered reveals
 * - Uses design system animation tokens
 * - Forwards ref for advanced use cases
 * - Lightweight wrapper with no performance impact
 *
 * Animation Details:
 * - Duration: 0.5s (defined in design tokens)
 * - Easing: ease-out
 * - Transform: translateY(20px) → translateY(0)
 * - Opacity: 0 → 1
 */
export const SectionReveal = forwardRef<HTMLDivElement, SectionRevealProps>(
  ({ delay = 0, className, children, style, ...props }, ref) => {
    const revealClasses = cn('section-reveal', className);

    // Merge animation delay with any existing inline styles
    const combinedStyle: React.CSSProperties = {
      animationDelay: `${delay}s`,
      ...style,
    };

    return (
      <div ref={ref} className={revealClasses} style={combinedStyle} {...props}>
        {children}
      </div>
    );
  }
);

SectionReveal.displayName = 'SectionReveal';
